/**
 * Page Tarifs v2 (SPEC § 4.6 : TA1, TA2, TA4) — habillage v2 de la page Tarifs administrable.
 *
 * Même structure que screens/Prices/components/ServicesContracts.tsx (v1, non modifié) : titre et
 * sous-titre du BO → blocs « top » → disposition `navigation` (segments PACKS / ABONNEMENTS,
 * sous-onglets, offres en studio) ou `flat` (catégories, offres en studio, abonnements) → bandeau
 * « exclusivité ». Mêmes requêtes (sale/services, sale/contracts?locationId), même fusion des
 * données (mergePricingService), mêmes règles de segments ; rendu selon la maquette /msc.
 *
 * TA4 : la mention « Ces crédits sont valables uniquement dans les studios de la région parisienne »
 * était ajoutée en v1 par le CSS du thème / du plugin (`.ServicesTitle::after { content: … }`), que
 * la v2 ne reçoit plus. La v2 affiche le sous-titre administrable du BO (Tarifs → Sous-titre,
 * commit cce190a) en chapô sous le titre : y saisir la mention sur les sites parisiens.
 */
import { Spinner } from '@chakra-ui/react';
import { useListContracts, useListServices } from 'hooks/useApiQueries';
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  mergePricingContract,
  mergePricingService,
  mergeTopBlock,
  type MergedPricingContract,
  type MergedPricingService,
} from 'screens/Prices/utils/mergePricingService';
import type { MindbodyContract } from 'types/contract';
import type {
  PricingCategory,
  PricingConfig,
  PricingContract,
  PricingContractsColumns,
  PricingExclusivityBanner,
  PricingGlobalTexts,
  PricingStudioOffers,
  PricingSubTab,
  PricingTopBlocks,
} from 'types/pricing';
import type { Service } from 'types/service';
import { ContractCard, OfferCard, OfferImage, PackCard } from './cards';
import { ContractTermsModal } from './ContractTermsModal';
import { Icon } from './icons';
import {
  activeSubTab,
  bySortOrder,
  frenchMoney,
  gridColumns,
  hasText,
  mentionsAlma,
  orphanStart,
  readableOnSetting,
  resolveSegments,
  visibleCategories,
  visibleContracts,
  type SegmentId,
} from './lib';
import { useBuyService, useSubscribe } from './useBuy';
import { useInk, useSitePalette } from './useInk';

/** Colonnes d'une grille (grand écran, puis au plus 2 sous 900 px de conteneur : prices.css). */
function gridStyle(cols: number): CSSProperties {
  return { '--mmv2-cols': cols, '--mmv2-cols-md': Math.min(cols, 2) } as CSSProperties;
}

/**
 * Trois colonnes ou plus : grille de la largeur de la page, bords alignés sur les blocs « top » et l'encadré « Offres
 * disponibles en studio » (revue du 05/10 : 3 × 400 + 2 × 16 = 1 232 px dans une page de 1 240, retrait de 4 px).
 * Le plafond de 400 px par carte ne vaut que pour 1 ou 2 cartes.
 */
/**
 * Fond et texte d'un segment ou d'un sous-onglet actif à la couleur réglée dans le BO (« Couleur » du segment / du
 * sous-onglet, comme la v1) : revue du 05/10, segment noir à côté des rubans et boutons verts de Dynamo Paris. Couleur
 * vide ou illisible : rien (encre de la maquette, ux-v2.css).
 */
function useActiveColorStyle(): (color: string | undefined) => CSSProperties | undefined {
  const ink = useInk();
  const palette = useSitePalette();
  return (color) => {
    const colors = readableOnSetting(color, ink, palette);
    return colors ? { background: colors.background, borderColor: colors.background, color: colors.color } : undefined;
  };
}

const gridClass = (cols: number) => `mmv2-prices__grid${cols >= 3 ? ' mmv2-prices__grid--fill' : ''}`;

/** Config injectée par Frontend.php (`pricingConfig`), comme ServicesContracts.tsx. */
function readPricingConfig(): PricingConfig | undefined {
  return (typeof mondayMindbody !== 'undefined' && mondayMindbody?.pricingConfig) as PricingConfig | undefined;
}

export function PricingPage() {
  const config = readPricingConfig();
  const { data: liveServices, isLoading, isError } = useListServices();

  // = ServicesContracts.tsx : contrats lus sur la location par défaut du BO, seulement s'il y en a.
  const hasContracts = (config?.contracts.length ?? 0) > 0;
  const { data: liveContracts, isLoading: contractsLoading } = useListContracts(
    hasContracts ? (config?.defaultLocationId ?? 1) : undefined,
  );

  const liveById = useMemo(() => {
    const map = new Map<string, Service>();
    (liveServices ?? []).forEach((svc) => {
      if (svc?.Id !== undefined && svc?.Id !== null) map.set(String(svc.Id), svc);
    });
    return map;
  }, [liveServices]);

  const liveContractById = useMemo(() => {
    const map = new Map<string, MindbodyContract>();
    (liveContracts ?? []).forEach((c) => {
      if (c?.Id !== undefined && c?.Id !== null) map.set(String(c.Id), c);
    });
    return map;
  }, [liveContracts]);

  const [termsOf, setTermsOf] = useState<MergedPricingContract | null>(null);

  if (!config) return null;

  const title = config.pageTitle || 'Tarifs';
  const header = (
    <header className="mmv2-prices__head">
      {/* Kicker « Tarifs » seulement si le titre du BO est un slogan (maquette : « Tarifs » + H1). */}
      {title.trim().toLowerCase() !== 'tarifs' ? <span className="mmv2-kicker mmv2-kicker--brand">Tarifs</span> : null}
      <h1 className="mmv2-prices__title">{title}</h1>
      {hasText(config.pageSubtitle) ? <p className="mmv2-prices__subtitle">{config.pageSubtitle}</p> : null}
    </header>
  );

  if (isLoading) {
    return (
      <div className="mmv2-prices__page">
        {header}
        <div className="mmv2-prices__state" role="status">
          <Spinner size="lg" color="mmv2.brand" thickness="3px" />
          <span>Chargement des tarifs…</span>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mmv2-prices__page">
        {header}
        <div className="mmv2-prices__state" role="alert">
          <span>Impossible de charger les tarifs pour le moment.</span>
        </div>
      </div>
    );
  }

  const orderedCategories = bySortOrder(config.categories);
  const globalTexts = config.globalTexts;
  const almaText = globalTexts?.almaCardText ?? '';
  const common = { liveById, liveContractById, globalTexts, almaText, contractsLoading, onDetails: setTermsOf };

  return (
    <div className="mmv2-prices__page">
      {header}

      {config.topBlocks.items.length > 0 ? (
        <TopBlocks topBlocks={config.topBlocks} liveById={liveById} globalTexts={globalTexts} almaText={almaText} />
      ) : null}

      {config.layout === 'navigation' ? (
        <NavigationLayout config={config} orderedCategories={orderedCategories} {...common} />
      ) : (
        <FlatLayout
          orderedCategories={orderedCategories}
          contracts={config.contracts}
          hasContracts={hasContracts}
          abonnementsLabel={config.segments?.abonnements?.label ?? 'Abonnements'}
          studioOffers={config.studioOffers}
          contractsColumns={config.contractsColumns}
          {...common}
        />
      )}

      <ExclusivityBanner banner={config.exclusivityBanner} />

      <ContractTermsModal merged={termsOf} onClose={() => setTermsOf(null)} />
    </div>
  );
}

interface CommonProps {
  liveById: Map<string, Service>;
  liveContractById: Map<string, MindbodyContract>;
  globalTexts: PricingGlobalTexts;
  almaText: string;
  contractsLoading: boolean;
  onDetails: (merged: MergedPricingContract) => void;
}

// ---------------------------------------------------------------------------------------------
// Dispositions
// ---------------------------------------------------------------------------------------------

function FlatLayout({
  orderedCategories,
  contracts,
  hasContracts,
  abonnementsLabel,
  studioOffers,
  contractsColumns,
  ...common
}: CommonProps & {
  orderedCategories: PricingCategory[];
  contracts: PricingContract[];
  hasContracts: boolean;
  abonnementsLabel: string;
  studioOffers: PricingStudioOffers;
  contractsColumns: PricingContractsColumns;
}) {
  return (
    <>
      {orderedCategories.map((category) => (
        <CategoryBlock key={category.id} category={category} {...common} />
      ))}
      <StudioOffers block={studioOffers} />
      {hasContracts ? (
        <ContractsBlock contracts={contracts} heading={abonnementsLabel} columns={contractsColumns} {...common} />
      ) : null}
    </>
  );
}

function NavigationLayout({
  config,
  orderedCategories,
  ...common
}: CommonProps & { config: PricingConfig; orderedCategories: PricingCategory[] }) {
  const { options, initial, showSelector } = useMemo(() => resolveSegments(config), [config]);
  /*
   * Segment dans l'adresse (#/tarifs?segment=abonnements, remplacée sans nouvelle entrée d'historique) : au retour du
   * panier (« Retour », pastille « Offre »), la page revient sur le segment choisi et non sur PACKS (revue du 05/10 ;
   * l'état local était remis à zéro au remontage).
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const fromUrl = searchParams.get('segment');
  const segment: SegmentId = options.some((option) => option.id === fromUrl) ? (fromUrl as SegmentId) : initial;
  const setSegment = (id: SegmentId) =>
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set('segment', id);
        next.delete('onglet');
        return next;
      },
      { replace: true },
    );
  const segments = config.segments;
  const activeStyle = useActiveColorStyle();
  // Pastille « Offre » d'un panier ouvert sans passer par cette page (Checkout/index) : page amenée sur les offres,
  // pas sur les offres « Première fois » du haut (revue du 05/10 : segment à 882 px sous la ligne de flottaison).
  const offersRef = useRef<HTMLElement>(null);
  const { state: locationState } = useLocation();
  const scrollToOffers = (locationState as { scrollToOffers?: boolean } | null)?.scrollToOffers === true;
  useEffect(() => {
    if (scrollToOffers) offersRef.current?.scrollIntoView({ block: 'start' });
  }, [scrollToOffers]);

  return (
    <section className="mmv2-prices__offers" aria-label="Offres" ref={offersRef}>
      {showSelector ? (
        <div className="mmv2-prices__switch">
          <div className="mmv2-seg" role="group" aria-label="Type d’offre">
            {options.map((option) => (
              <button
                key={option.id}
                type="button"
                aria-pressed={segment === option.id}
                data-segment={option.id}
                onClick={() => setSegment(option.id)}
                // Couleur du segment réglée dans le BO (comme la v1, SegmentedControl) ; vide : encre (maquette).
                style={segment === option.id ? activeStyle(segments[option.id]?.color) : undefined}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {/* Intitulé du segment affiché, pour la hiérarchie des titres (h1 → h2 → cartes h3). */}
      <h2 className="mmv2-prices__sr">{options.find((o) => o.id === segment)?.label}</h2>

      {segment === 'packs' ? (
        <SegmentBody
          intro={segments.packs?.introText ?? ''}
          subTabs={segments.packs?.subTabs ?? []}
          render={(subTab) => (
            <>
              {visibleCategories(orderedCategories, subTab).map((category) => (
                <CategoryBlock key={category.id} category={category} hideHeader {...common} />
              ))}
              <StudioOffers block={config.studioOffers} />
            </>
          )}
        />
      ) : (
        <SegmentBody
          intro={segments.abonnements?.introText ?? ''}
          subTabs={segments.abonnements?.subTabs ?? []}
          render={(subTab) => (
            <ContractsBlock
              contracts={visibleContracts(config.contracts, subTab)}
              columns={config.contractsColumns}
              {...common}
            />
          )}
        />
      )}
    </section>
  );
}

/** = SegmentBody v1 : chapô du segment, sous-onglets (étiquettes), chapô du sous-onglet, contenu. */
function SegmentBody({
  intro,
  subTabs,
  render,
}: {
  intro: string;
  subTabs: PricingSubTab[];
  render: (subTab: PricingSubTab | null) => ReactNode;
}) {
  const ordered = useMemo(() => bySortOrder(subTabs), [subTabs]);
  // Sous-onglet dans l'adresse aussi (#/tarifs?segment=…&onglet=…), même raison que le segment.
  const [searchParams, setSearchParams] = useSearchParams();
  const fromUrl = searchParams.get('onglet');
  const activeId = ordered.some((tab) => String(tab.id) === fromUrl) ? fromUrl : (ordered[0]?.id ?? null);
  const setActiveId = (id: string) =>
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set('onglet', String(id));
        return next;
      },
      { replace: true },
    );
  const active = activeSubTab(ordered, activeId);
  const activeStyle = useActiveColorStyle();

  return (
    <div className="mmv2-prices__segment">
      {hasText(intro) ? <p className="mmv2-prices__intro">{intro}</p> : null}
      {/* Un seul sous-onglet : rien à choisir, son libellé devient un intitulé (pas un faux bouton). */}
      {ordered.length === 1 ? <p className="mmv2-kicker mmv2-prices__subtab-label">{ordered[0].label}</p> : null}
      {ordered.length > 1 ? (
        <div className="mmv2-chips mmv2-prices__subtabs" role="group" aria-label="Formules">
          {ordered.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className="mmv2-chip"
              aria-pressed={active?.id === tab.id}
              onClick={() => setActiveId(tab.id)}
              style={active?.id === tab.id ? activeStyle(tab.color) : undefined}
            >
              {tab.label}
            </button>
          ))}
        </div>
      ) : null}
      {hasText(active?.introText) ? <p className="mmv2-prices__intro">{active?.introText}</p> : null}
      {render(ordered.length > 0 ? active : null)}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Blocs
// ---------------------------------------------------------------------------------------------

function TopBlocks({
  topBlocks,
  liveById,
  globalTexts,
  almaText,
}: {
  topBlocks: PricingTopBlocks;
  liveById: Map<string, Service>;
  globalTexts: PricingGlobalTexts;
  almaText: string;
}) {
  const buy = useBuyService();
  const merged = useMemo(
    () =>
      bySortOrder(topBlocks.items)
        .map((b) => mergeTopBlock(b, globalTexts, b.mindbodyId ? liveById.get(String(b.mindbodyId)) : undefined))
        .filter((m): m is MergedPricingService => m !== null),
    [topBlocks.items, globalTexts, liveById],
  );

  if (merged.length === 0) return null;

  // = TopBlocksRow v1 : photo centrale seulement entre exactement deux blocs.
  const centralImageUrl = topBlocks.centralImage?.url;
  const withImage = !!centralImageUrl && merged.length === 2;
  const reserveBand = merged.some((m) => m.ribbon && hasText(m.ribbon.text));
  const topColumns = withImage ? 3 : gridColumns(merged.length, 4);
  // Sans photo centrale : grille en demi-colonnes, cartes seules de leur rangée centrées, au bureau (topColumns) comme
  // à deux de front (≤ 1 100 px de conteneur pour 4 blocs et plus, ≤ 900 px sinon) ; une colonne sous 600 px.
  const place = (index: number): CSSProperties | undefined =>
    withImage
      ? undefined
      : ({
          '--mmv2-o-lg': orphanStart(index, merged.length, topColumns),
          '--mmv2-o-md': orphanStart(index, merged.length, Math.min(topColumns, 2)),
        } as CSSProperties);
  const card = (m: MergedPricingService, index = 0) => (
    <OfferCard
      key={m.id}
      merged={m}
      almaText={almaText}
      reserveBand={reserveBand}
      place={place(index)}
      onBuy={m.service ? () => buy(m.service as Service) : undefined}
    />
  );

  return (
    <section className="mmv2-prices__top" aria-label="Offres à la une">
      <div
        className={`${gridClass(topColumns)} mmv2-prices__grid--top${withImage ? ' mmv2-prices__grid--with-image' : ' mmv2-prices__grid--centered'}${
          topColumns === 4 ? ' mmv2-prices__grid--four' : ''
        }`}
        style={gridStyle(topColumns)}
      >
        {withImage ? (
          <>
            {card(merged[0])}
            <OfferImage url={centralImageUrl as string} />
            {card(merged[1])}
          </>
        ) : (
          merged.map((m, index) => card(m, index))
        )}
      </div>
    </section>
  );
}

function CategoryBlock({
  category,
  liveById,
  globalTexts,
  almaText,
  hideHeader,
}: CommonProps & { category: PricingCategory; hideHeader?: boolean }) {
  const buy = useBuyService();
  const merged = useMemo(
    () =>
      bySortOrder(category.services)
        .map((svc) => mergePricingService(svc, category, globalTexts, liveById.get(String(svc.mindbodyId))))
        .filter((m): m is MergedPricingService => m !== null),
    [category, globalTexts, liveById],
  );

  if (merged.length === 0) return null;

  const reserveBand = merged.some((m) => m.ribbon && hasText(m.ribbon.text));
  const almaFooter = globalTexts?.almaFooterText ?? '';

  return (
    <div className="mmv2-prices__category" data-category={category.id}>
      {!hideHeader ? (
        <div className="mmv2-prices__category-head">
          <h2 className="mmv2-prices__h2">{category.label}</h2>
          {hasText(category.introText) ? <p className="mmv2-prices__intro">{category.introText}</p> : null}
        </div>
      ) : null}
      <div className={gridClass(gridColumns(merged.length, 3))} style={gridStyle(gridColumns(merged.length, 3))}>
        {merged.map((m) => (
          <PackCard
            key={m.id}
            merged={m}
            almaText={almaText}
            reserveBand={reserveBand}
            onBuy={m.service ? () => buy(m.service as Service) : undefined}
          />
        ))}
      </div>
      {/* = AlmaMention « footer » v1 : phrase du BO, le mot « Alma » à la place du logo. */}
      {merged.some((m) => m.almaEnabled) && hasText(almaFooter) ? (
        <p className="mmv2-prices__alma">
          {almaFooter}
          {mentionsAlma(almaFooter) ? null : (
            <>
              {' '}
              <b className="mmv2-alma">Alma</b>
            </>
          )}
        </p>
      ) : null}
    </div>
  );
}

function ContractsBlock({
  contracts,
  liveContractById,
  liveById,
  globalTexts,
  contractsLoading,
  heading,
  columns,
  onDetails,
}: CommonProps & { contracts: PricingContract[]; heading?: string; columns?: PricingContractsColumns }) {
  const subscribe = useSubscribe();
  const merged = useMemo(
    () =>
      contractsLoading
        ? []
        : bySortOrder(contracts)
            .map((c) =>
              mergePricingContract(c, globalTexts, liveContractById.get(String(c.mindbodyContractId)), liveById),
            )
            .filter((m): m is MergedPricingContract => m !== null),
    [contracts, globalTexts, liveContractById, liveById, contractsLoading],
  );

  if (contractsLoading) {
    return (
      <div className="mmv2-prices__state" role="status">
        <Spinner size="md" color="mmv2.brand" thickness="3px" />
        <span>Chargement des abonnements…</span>
      </div>
    );
  }

  if (merged.length === 0) return null;

  const reserveBand = merged.some((m) => m.ribbon && hasText(m.ribbon.text));

  return (
    <div className="mmv2-prices__contracts">
      {heading ? (
        <div className="mmv2-prices__category-head">
          <h2 className="mmv2-prices__h2">{heading}</h2>
        </div>
      ) : null}
      <div
        className={gridClass(gridColumns(merged.length, columns ?? 3))}
        style={gridStyle(gridColumns(merged.length, columns ?? 3))}
      >
        {merged.map((m) => (
          <ContractCard
            key={m.id}
            merged={m}
            reserveBand={reserveBand}
            onSubscribe={() => subscribe(m)}
            onDetails={() => onDetails(m)}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * « Offres disponibles en studio » (= StudioOffersBlock v1), en encadré d'information comme la
 * notice « tarifs étudiants » de la maquette : ruban du BO en intitulé, titre, lignes, offres.
 */
function StudioOffers({ block }: { block: PricingStudioOffers }) {
  if (!block?.enabled) return null;
  const items = bySortOrder(block.items ?? []);
  const lines = (block.introLines ?? []).filter(hasText);
  const label = block.ribbon && hasText(block.ribbon.text) ? block.ribbon.text : null;

  return (
    <aside
      className="mmv2-notice mmv2-prices__studio"
      aria-label={label ?? (hasText(block.title) ? block.title : 'Offres en studio')}
    >
      <Icon name="info" />
      <div className="mmv2-prices__studio-body">
        {label ? <span className="mmv2-prices__studio-label">{label}</span> : null}
        {hasText(block.title) ? <b className="mmv2-prices__studio-title">{block.title}</b> : null}
        {lines.map((line, i) => (
          <p key={i}>{line}</p>
        ))}
        {items.length > 0 ? (
          <div className="mmv2-prices__studio-items">
            {items.map((item) => (
              <div key={item.id} className="mmv2-prices__studio-item">
                <b>{item.title}</b>
                {hasText(item.price) ? (
                  <span className="mmv2-prices__studio-price">
                    {frenchMoney(`${item.price}€`)}
                    {item.count > 1 ? <small> ({item.count} séances)</small> : null}
                  </span>
                ) : null}
                {hasText(item.validity) ? <span>{item.validity}</span> : null}
                {hasText(item.locations) ? <span>{item.locations}</span> : null}
                {hasText(item.description) ? <span>{item.description}</span> : null}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </aside>
  );
}

/** « Exclusivité studio » (= ExclusivityBanner v1) : bloc sombre en bas de page. */
function ExclusivityBanner({ banner }: { banner: PricingExclusivityBanner }) {
  if (!banner?.enabled) return null;
  if (!hasText(banner.title) && !hasText(banner.body)) return null;
  return (
    <section className="mmv2-prices__excl">
      {hasText(banner.title) ? <h2 className="mmv2-prices__excl-title">{banner.title}</h2> : null}
      {hasText(banner.body) ? <p className="mmv2-prices__excl-body">{banner.body}</p> : null}
    </section>
  );
}
